import { useMutation, useQueryClient } from '@tanstack/react-query';
import { branchOfficeService } from '../services/branchOfficeService';
import { useReportError } from '@features/support/SupportErrorProvider';
import { extractErrorInfo } from '@features/support/errorInfo';
import type {
  CreateAddressPayload,
  CreateBranchOfficePayload,
  GeolocationData,
  AddressResponse,
  BranchOffice,
} from '../types/branchOffice.types';

/**
 * Hook to fetch geolocation data by coordinates
 */
export function useGeolocationByCoordinates() {
  return useMutation<GeolocationData, Error, { latitude: string; longitude: string }>({
    mutationFn: ({ latitude, longitude }) =>
      branchOfficeService.getGeolocationByCoordinates(latitude, longitude),
  });
}

/**
 * Hook to create an address
 */
export function useCreateAddress() {
  return useMutation<AddressResponse, Error, CreateAddressPayload>({
    mutationFn: (payload) => branchOfficeService.createAddress(payload),
  });
}

/**
 * Hook to create a branch office
 */
export function useCreateBranchOffice() {
  const queryClient = useQueryClient();
  const reportError = useReportError();

  return useMutation<BranchOffice, Error, CreateBranchOfficePayload>({
    mutationFn: (payload) => branchOfficeService.createBranchOffice(payload),
    onSuccess: () => {
      // Invalidate branch offices query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['branchOffices'] });
    },
    onError: (error: any) => {
      reportError({ title: 'Error al crear sucursal', screen: 'crear sucursal', ...extractErrorInfo(error) });
    },
  });
}
